import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';
import { cx } from './classNames';
import styles from './Button.module.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  loading?: boolean;
  leadingIcon?: IconName;
  leadingIconDirectional?: boolean;
  /**
   * R4-01: Sprite-Icon NACH der Beschriftung (Assistenten-„Weiter"). Der Knopf
   * ist inline-flex mit align-items:center — das Icon sitzt damit bauartbedingt
   * auf der optischen Mitte und kann sich nie zum Text verschieben. Vorher
   * steckten die Pfeile als Zeichen (U+2190/U+2192) IN den Uebersetzungswerten
   * und wurden per Glyph-Fallback aus einer Fremdschrift auf der
   * Mathematik-Achse gesetzt — rund 1,5 px zu tief und ohne fette Variante.
   */
  trailingIcon?: IconName;
  trailingIconDirectional?: boolean;
  children: ReactNode;
}

function visibleContent(children: ReactNode): ReactNode {
  if (typeof children !== 'string') return children;
  return children.replace(/^\s*\+\s+/, '');
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  leadingIcon,
  leadingIconDirectional = false,
  trailingIcon,
  trailingIconDirectional = false,
  className,
  children,
  type = 'button',
  ...buttonProps
}: ButtonProps) {
  const isDisabled = loading ? true : disabled;
  const content = visibleContent(children);

  return (
    <button
      {...buttonProps}
      type={type}
      className={cx(styles.button, styles[variant], styles[size], fullWidth && styles.fullWidth, className)}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {loading ? <Spinner decorative className={styles.icon} /> : null}
      {!loading && leadingIcon ? (
        <Icon name={leadingIcon} className={styles.icon} size={18} directional={leadingIconDirectional} />
      ) : null}
      <span className={loading ? styles.loadingContent : undefined}>{content}</span>
      {!loading && trailingIcon ? (
        <Icon name={trailingIcon} className={styles.icon} size={18} directional={trailingIconDirectional} />
      ) : null}
    </button>
  );
}
